import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaCheck, FaShippingFast } from 'react-icons/fa';
import { MdIron } from 'react-icons/md';
import { GiClothes } from 'react-icons/gi';
import PricingBanner from '../components/PricingBanner';

const ServicesContainer = styled.div`
  padding: 80px 0 60px;
  background: var(--background-dark);
`;

const ServiceHero = styled.div`
  background: linear-gradient(135deg, var(--background-accent) 0%, var(--background-lighter) 100%);
  padding: 60px 0;
  text-align: center;
  margin-bottom: 80px;
  border-bottom: 5px solid var(--primary-color);
`;

const ServiceHeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
`;

const ServiceHeroTitle = styled(motion.h1)`
  font-size: 2.8rem;
  color: var(--text-primary);
  margin-bottom: 20px;
  
  span {
    color: var(--primary-color);
  }
`;

const ServiceHeroDescription = styled(motion.p)`
  color: var(--text-secondary);
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 0;
`;

const ServiceSection = styled.section`
  padding: 60px 0;
  background-color: ${props => props.$dark ? 'var(--background-darker)' : 'var(--background-dark)'};
`;

const ServiceContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  align-items: center;
  gap: 40px;
  
  @media (max-width: 768px) {
    flex-direction: ${props => props.$reverse ? 'column-reverse' : 'column'};
  }
`;

const ServiceInfo = styled.div`
  flex: 1;
`;

const ServiceImageContainer = styled.div`
  flex: 1;
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const ServiceImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  border-radius: var(--border-radius);
  transition: transform 0.5s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const ServiceTitle = styled(motion.h2)`
  font-size: 2.2rem;
  color: var(--text-primary);
  margin-bottom: 20px;
  
  span {
    color: var(--primary-color);
  }
`;

const ServiceDescription = styled(motion.p)`
  color: var(--text-secondary);
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 25px;
`;

const ServiceFeatures = styled(motion.ul)`
  list-style: none;
  padding: 0;
  margin-bottom: 25px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 15px;
`;

const ServiceFeature = styled(motion.li)`
  display: flex;
  align-items: center;
  color: var(--text-secondary);
  
  svg {
    color: var(--primary-color);
    margin-right: 10px;
    font-size: 1.2rem;
  }
`;

const OtherServicesSection = styled.section`
  padding: 80px 0;
  background-color: var(--background-dark);
`;

const OtherServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const OtherServiceCard = styled(motion.div)`
  background-color: var(--background-lighter);
  border-radius: var(--border-radius);
  padding: 30px;
  text-align: center;
  transition: var(--transition);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
`;

const OtherServiceIcon = styled.div`
  width: 60px;
  height: 60px;
  background-color: rgba(107, 99, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  
  svg {
    font-size: 1.8rem;
    color: var(--primary-color);
  }
`;

const OtherServiceTitle = styled.h3`
  font-size: 1.3rem;
  color: var(--text-primary);
  margin-bottom: 15px;
`;

const OtherServiceDescription = styled.p`
  color: var(--text-secondary);
  font-size: 0.9rem;
  line-height: 1.6;
`;

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const Services = () => {
  return (
    <ServicesContainer>
      <ServiceHero>
        <ServiceHeroContent>
          <ServiceHeroTitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Our <span>Premium</span> Services
          </ServiceHeroTitle>
          <ServiceHeroDescription
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            At Istri Wala, we specialize in premium steam ironing services - our core expertise and passion. We also offer comprehensive dry cleaning, complete laundry solutions, and professional shoe cleaning to meet all your garment care needs with the same dedication to quality.
          </ServiceHeroDescription>
        </ServiceHeroContent>
      </ServiceHero>

      <ServiceSection>
        <ServiceContent>
          <ServiceInfo>
            <ServiceTitle
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              Premium <span>Steam Ironing</span> Service
            </ServiceTitle>
            <ServiceDescription
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Our specialty and pride! Professional steam ironing service that ensures your clothes look crisp, fresh, and perfectly pressed. Using state-of-the-art steam equipment and expert techniques perfected over years, we remove even the most stubborn wrinkles while being gentle on all fabric types. Every garment receives individual attention for flawless results.
            </ServiceDescription>
            
            <ServiceFeatures
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <ServiceFeature variants={fadeInUp}>
                <FaCheck /> Professional pressing
              </ServiceFeature>
              <ServiceFeature variants={fadeInUp}>
                <FaCheck /> Wrinkle-free guarantee
              </ServiceFeature>
              <ServiceFeature variants={fadeInUp}>
                <FaCheck /> Gentle on all fabrics
              </ServiceFeature>
              <ServiceFeature variants={fadeInUp}>
                <FaCheck /> Same-day service available
              </ServiceFeature>
              <ServiceFeature variants={fadeInUp}>
                <FaCheck /> Careful handling
              </ServiceFeature>
              <ServiceFeature variants={fadeInUp}>
                <FaCheck /> Starch options available
              </ServiceFeature>
            </ServiceFeatures>
          </ServiceInfo>
          
          <ServiceImageContainer>
            <ServiceImage 
              src="https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80" 
              alt="Steam Ironing Service"
            />
          </ServiceImageContainer>
        </ServiceContent>
      </ServiceSection>

      <ServiceSection $dark>
        <ServiceContent $reverse>
          <ServiceImageContainer>
            <ServiceImage 
              src="https://images.unsplash.com/photo-1489274495757-95c7c837b101?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80" 
              alt="Shirt Pressing Service"
            />
          </ServiceImageContainer>
          
          <ServiceInfo>
            <ServiceTitle
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              Expert <span>Dry Cleaning</span> Service
            </ServiceTitle>
            <ServiceDescription
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Professional dry cleaning service for delicate fabrics, suits, formal wear, and special garments. We use eco-friendly solvents and advanced cleaning techniques to ensure your clothes are impeccably clean without damage.
            </ServiceDescription>
            
            <ServiceFeatures
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <ServiceFeature variants={fadeInUp}>
                <FaCheck /> Eco-friendly solvents
              </ServiceFeature>
              <ServiceFeature variants={fadeInUp}>
                <FaCheck /> Delicate fabric care
              </ServiceFeature>
              <ServiceFeature variants={fadeInUp}>
                <FaCheck /> Stain removal expertise
              </ServiceFeature>
              <ServiceFeature variants={fadeInUp}>
                <FaCheck /> Suits & formal wear
              </ServiceFeature>
              <ServiceFeature variants={fadeInUp}>
                <FaCheck /> Professional finishing
              </ServiceFeature>
              <ServiceFeature variants={fadeInUp}>
                <FaCheck /> Protective packaging
              </ServiceFeature>
            </ServiceFeatures>
          </ServiceInfo>
        </ServiceContent>
      </ServiceSection>

      <ServiceSection>
        <ServiceContent>
          <ServiceInfo>
            <ServiceTitle
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              Complete <span>Laundry</span> Service
            </ServiceTitle>
            <ServiceDescription
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Our complete laundry service includes washing, drying, and folding with premium detergents and fabric softeners. We handle everything from everyday wear to bed linens, ensuring your items are fresh, clean, and perfectly folded.
            </ServiceDescription>
            
            <ServiceFeatures
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <ServiceFeature variants={fadeInUp}>
                <FaCheck /> Premium detergents used
              </ServiceFeature>
              <ServiceFeature variants={fadeInUp}>
                <FaCheck /> Wash, dry & fold service
              </ServiceFeature>
              <ServiceFeature variants={fadeInUp}>
                <FaCheck /> Fabric softener included
              </ServiceFeature>
              <ServiceFeature variants={fadeInUp}>
                <FaCheck /> Color separation
              </ServiceFeature>
              <ServiceFeature variants={fadeInUp}>
                <FaCheck /> Hygienic processing
              </ServiceFeature>
              <ServiceFeature variants={fadeInUp}>
                <FaCheck /> Neatly folded delivery
              </ServiceFeature>
            </ServiceFeatures>
          </ServiceInfo>
          
          <ServiceImageContainer>
            <ServiceImage 
              src="https://images.unsplash.com/photo-1445510861639-5651173bc5d5?ixlib=rb-1.2.1&auto=format&fit=crop&w=1951&q=80" 
              alt="Professional Pressing Service"
            />
          </ServiceImageContainer>
        </ServiceContent>
      </ServiceSection>

      <ServiceSection $dark>
        <ServiceContent $reverse>
          <ServiceImageContainer>
            <ServiceImage 
              src="https://images.unsplash.com/photo-1460353581641-37baddab0fa2?ixlib=rb-4.0.3&auto=format&fit=crop&w=1350&q=80" 
              alt="Shoes Cleaning Service"
            />
          </ServiceImageContainer>
          
          <ServiceInfo>
            <ServiceTitle
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              Professional <span>Shoes Cleaning</span> Service
            </ServiceTitle>
            <ServiceDescription
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Expert shoe cleaning and restoration service for all types of footwear. From sneakers to formal shoes, we restore your footwear to its original condition with specialized cleaning techniques and premium products.
            </ServiceDescription>
            
            <ServiceFeatures
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <ServiceFeature variants={fadeInUp}>
                <FaCheck /> All footwear types
              </ServiceFeature>
              <ServiceFeature variants={fadeInUp}>
                <FaCheck /> Deep cleaning process
              </ServiceFeature>
              <ServiceFeature variants={fadeInUp}>
                <FaCheck /> Stain & odor removal
              </ServiceFeature>
              <ServiceFeature variants={fadeInUp}>
                <FaCheck /> Leather conditioning
              </ServiceFeature>
              <ServiceFeature variants={fadeInUp}>
                <FaCheck /> Color restoration
              </ServiceFeature>
              <ServiceFeature variants={fadeInUp}>
                <FaCheck /> Protective treatment
              </ServiceFeature>
            </ServiceFeatures>
          </ServiceInfo>
        </ServiceContent>
      </ServiceSection>

      <OtherServicesSection>
        <div className="section-title">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Additional Services
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Additional specialized services to complement our main offerings
          </motion.p>
        </div>
        
        <OtherServicesGrid>
          <OtherServiceCard
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <OtherServiceIcon>
              <MdIron />
            </OtherServiceIcon>
            <OtherServiceTitle>Shirt & Trouser Pressing</OtherServiceTitle>
            <OtherServiceDescription>
              Specialized pressing for shirts and trousers with perfect collar, cuff finishing and sharp creases for a professional look.
            </OtherServiceDescription>
          </OtherServiceCard>
          
          <OtherServiceCard
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <OtherServiceIcon>
              <GiClothes />
            </OtherServiceIcon>
            <OtherServiceTitle>Alterations & Repairs</OtherServiceTitle>
            <OtherServiceDescription>
              Basic alterations and minor repairs for your garments, including button replacement, hem adjustments, and small fixes.
            </OtherServiceDescription>
          </OtherServiceCard>
          
          <OtherServiceCard
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <OtherServiceIcon>
              <FaShippingFast />
            </OtherServiceIcon>
            <OtherServiceTitle>Express Service</OtherServiceTitle>
            <OtherServiceDescription>
              Quick turnaround for urgent needs, with same-day service available for orders placed before noon.
            </OtherServiceDescription>
          </OtherServiceCard>
        </OtherServicesGrid>
      </OtherServicesSection>
      
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px 40px' }}>
        <PricingBanner
          title="Ready to Experience Premium Service?"
          description="Place your order now and enjoy professional laundry services with free pickup and delivery!"
          discount="Order Now"
          buttonText="Place Order"
          buttonLink="/select-items"
          icon="percent"
        />
      </div>
    </ServicesContainer>
  );
};

export default Services;
